import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface CommissionPlan {
  id: string;
  name: string;
  business_unit: string;
  effective_from: string;
  effective_to: string | null;
  commission_base: string;
  min_entry_pct: number;
  rampup_type: string | null;
  rampup_duration_days: number;
  rampup_config: Record<string, number>;
  max_accelerator_pct: number;
  status: string;
  created_at: string;
  updated_at: string;
}

export interface CommissionTier {
  id: string;
  plan_id: string;
  min_goal_pct: number;
  max_goal_pct: number | null;
  commission_pct: number;
  sort_order: number;
}

export interface CommissionAccelerator {
  id: string;
  plan_id: string;
  name: string;
  key: string;
  bonus_pct: number;
  description: string | null;
  threshold_config: Record<string, unknown>;
}

export interface CommissionPlanWithDetails extends CommissionPlan {
  commission_tiers: CommissionTier[];
  commission_accelerators: CommissionAccelerator[];
}

export function useCommissionPlans(businessUnit?: string) {
  return useQuery({
    queryKey: ['commission-plans', businessUnit],
    queryFn: async () => {
      let query = supabase
        .from('commission_plans')
        .select('*, commission_tiers(*), commission_accelerators(*)')
        .order('effective_from', { ascending: false });

      if (businessUnit) {
        query = query.eq('business_unit', businessUnit);
      }

      const { data, error } = await query;
      if (error) throw error;
      
      return (data as unknown as CommissionPlanWithDetails[]).map(plan => ({
        ...plan,
        commission_tiers: (plan.commission_tiers || []).sort((a, b) => a.sort_order - b.sort_order),
      }));
    },
  });
}

export function useCommissionPlan(planId: string | undefined) {
  return useQuery({
    queryKey: ['commission-plan', planId],
    queryFn: async () => {
      if (!planId) return null;
      const { data, error } = await supabase
        .from('commission_plans')
        .select('*, commission_tiers(*), commission_accelerators(*)')
        .eq('id', planId)
        .single();
      if (error) throw error;
      const plan = data as unknown as CommissionPlanWithDetails;
      return {
        ...plan,
        commission_tiers: (plan.commission_tiers || []).sort((a, b) => a.sort_order - b.sort_order),
      };
    },
    enabled: !!planId,
  });
}

export function useCreateCommissionPlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: {
      plan: Omit<CommissionPlan, 'id' | 'created_at' | 'updated_at'>;
      tiers: Omit<CommissionTier, 'id' | 'plan_id'>[];
      accelerators: Omit<CommissionAccelerator, 'id' | 'plan_id'>[];
    }) => {
      const { data: plan, error: planError } = await supabase
        .from('commission_plans')
        .insert(payload.plan as any)
        .select()
        .single();
      if (planError) throw planError;

      if (payload.tiers.length > 0) {
        const { error: tiersError } = await supabase
          .from('commission_tiers')
          .insert(payload.tiers.map(t => ({ ...t, plan_id: plan.id })) as any);
        if (tiersError) throw tiersError;
      }

      if (payload.accelerators.length > 0) {
        const { error: accError } = await supabase
          .from('commission_accelerators')
          .insert(payload.accelerators.map(a => ({ ...a, plan_id: plan.id })) as any);
        if (accError) throw accError;
      }

      return plan;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['commission-plans'] });
      toast.success('Plano de comissão criado com sucesso');
    },
    onError: (error: Error) => {
      toast.error(`Erro ao criar plano: ${error.message}`);
    },
  });
}

export function useUpdateCommissionPlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: {
      planId: string;
      plan: Partial<CommissionPlan>;
      tiers?: Omit<CommissionTier, 'id' | 'plan_id'>[];
      accelerators?: Omit<CommissionAccelerator, 'id' | 'plan_id'>[];
    }) => {
      const { error: planError } = await supabase
        .from('commission_plans')
        .update({ ...payload.plan, updated_at: new Date().toISOString() } as any)
        .eq('id', payload.planId);
      if (planError) throw planError;

      if (payload.tiers) {
        await supabase.from('commission_tiers').delete().eq('plan_id', payload.planId);
        if (payload.tiers.length > 0) {
          const { error } = await supabase
            .from('commission_tiers')
            .insert(payload.tiers.map(t => ({ ...t, plan_id: payload.planId })) as any);
          if (error) throw error;
        }
      }

      if (payload.accelerators) {
        await supabase.from('commission_accelerators').delete().eq('plan_id', payload.planId);
        if (payload.accelerators.length > 0) {
          const { error } = await supabase
            .from('commission_accelerators')
            .insert(payload.accelerators.map(a => ({ ...a, plan_id: payload.planId })) as any);
          if (error) throw error;
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['commission-plans'] });
      queryClient.invalidateQueries({ queryKey: ['commission-plan'] });
      toast.success('Plano atualizado com sucesso');
    },
    onError: (error: Error) => {
      toast.error(`Erro ao atualizar plano: ${error.message}`);
    },
  });
}

export function useDeleteCommissionPlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (planId: string) => {
      const { error } = await supabase
        .from('commission_plans')
        .delete()
        .eq('id', planId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['commission-plans'] });
      toast.success('Plano removido');
    },
    onError: (error: Error) => {
      toast.error(`Erro ao remover plano: ${error.message}`);
    },
  });
}

/**
 * Get the active commission plan for a given month and business unit
 */
export function useActiveCommissionPlan(yearMonth: string, businessUnit: string = 'us') {
  return useQuery({
    queryKey: ['commission-plan-active', yearMonth, businessUnit],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('commission_plans')
        .select('*, commission_tiers(*), commission_accelerators(*)')
        .eq('business_unit', businessUnit)
        .eq('status', 'active')
        .lte('effective_from', yearMonth)
        .or(`effective_to.is.null,effective_to.gte.${yearMonth}`)
        .order('effective_from', { ascending: false })
        .limit(1);

      if (error) throw error;
      if (!data || data.length === 0) return null;

      const plan = data[0] as unknown as CommissionPlanWithDetails;
      return {
        ...plan,
        commission_tiers: (plan.commission_tiers || []).sort((a, b) => a.sort_order - b.sort_order),
      };
    },
    enabled: !!yearMonth,
  });
}
